import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LOGO_URL } from "../lib/brand";
import { LogOut, Send, X, Mail, Clock } from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

const ALL_GROUPS = [
  { id: "A", label: "Gruppe A", desc: "Insider-Report Leads" },
  { id: "B", label: "Gruppe B", desc: "Neue Anfragen" },
  { id: "C", label: "Gruppe C", desc: "Bereits kontaktiert" },
  { id: "D", label: "Gruppe D", desc: "Bestehende Kunden" },
];

const PRESETS = [
  { label: "Schnell — 1 Tag = 10 Sek", value: 10, total: "≈ 10 Min" },
  { label: "Mittel — 1 Tag = 30 Sek (Standard)", value: 30, total: "≈ 30 Min" },
  { label: "Langsam — 1 Tag = 60 Sek", value: 60, total: "≈ 1 Std" },
  { label: "Realistisch — 1 Tag = 5 Min", value: 300, total: "≈ 5 Std" },
  { label: "Echtzeit — 1 Tag = 1 Tag", value: 86400, total: "≈ 60 Tage" },
];

export default function AdminMailTest() {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [secondsPerDay, setSecondsPerDay] = useState(30);
  const [groups, setGroups] = useState(["A", "B", "C", "D"]);
  const [active, setActive] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);

  const headers = useMemo(() => ({ "X-Admin-Token": token || "" }), [token]);

  useEffect(() => {
    if (!token) navigate("/admin/login");
  }, [token, navigate]);

  const loadActive = async () => {
    try {
      const r = await axios.get(`${API}/admin/drip/test-active`, { headers });
      setActive(r.data?.active || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadActive();
    const t = setInterval(loadActive, 4000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const start = async () => {
    if (!email) return toast.error("E-Mail-Adresse fehlt");
    if (groups.length === 0) return toast.error("Mindestens eine Gruppe auswählen");
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/drip/test-run`,
        { email, name: name || null, seconds_per_day: secondsPerDay, groups },
        { headers }
      );
      setLastSummary(r.data?.summary || null);
      toast.success(`Test gestartet — ${r.data?.summary?.total_mails || 0} Mails an ${email}`);
      loadActive();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Starten");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (cancelEmail) => {
    try {
      await axios.post(
        `${API}/admin/drip/test-cancel`,
        { email: cancelEmail },
        { headers }
      );
      toast.success(`Test für ${cancelEmail} abgebrochen`);
      loadActive();
    } catch {
      toast.error("Abbruch fehlgeschlagen");
    }
  };

  const toggleGroup = (g) => {
    setGroups((cur) => (cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]));
  };

  const logout = () => {
    localStorage.removeItem("alphalane_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="admin-mail-test">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]">
              <span className="al-grad-text">ALPHALANE</span>
              <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">Admin · Mail-Test</span>
            </div>
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="al-cta-ghost !py-2 !px-3 text-sm">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <AdminTopNav />

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8 space-y-8">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Mailserien-Test</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Sendet alle Mails der gewählten Serien (A/B/C/D) auf einer gewählten Zeitachse an deine Adresse —
            jede Mail mit dem Betreff-Prefix <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">[TEST X·Y]</code>.
            Schreibt nichts in <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">email_history</code> und beeinflusst keine echten Kunden.
            Die Mails liegen in einer DB-Queue und werden alle 30 Sek. vom Scheduler abgesetzt — auch wenn das Backend zwischenzeitlich neu startet.
          </p>
        </div>

        {/* Form */}
        <section className="al-card p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Empfänger-E-Mail</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="deine@adresse.com"
                data-testid="mail-test-email"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[var(--al-purple)] outline-none text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Name (optional)</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Pascal"
                data-testid="mail-test-name"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[var(--al-purple)] outline-none text-sm"
              />
            </label>
          </div>

          <div>
            <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Zeit-Kompression</span>
            <div className="mt-2 grid sm:grid-cols-2 gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setSecondsPerDay(p.value)}
                  data-testid={`preset-${p.value}`}
                  className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${
                    secondsPerDay === p.value
                      ? "border-[var(--al-purple)] bg-violet-50 text-slate-900 font-medium"
                      : "border-slate-200 hover:border-slate-300 text-slate-700"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span>{p.label}</span>
                    <span className="text-xs text-slate-500">{p.total}</span>
                  </div>
                </button>
              ))}
            </div>
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={secondsPerDay}
              onChange={(e) => setSecondsPerDay(parseFloat(e.target.value) || 30)}
              data-testid="seconds-per-day"
              className="mt-2 w-44 px-3 py-2 rounded-lg border border-slate-200 outline-none text-sm"
            />
            <span className="ml-2 text-xs text-slate-500">Sekunden pro Tag</span>
          </div>

          <div>
            <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Serien</span>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {ALL_GROUPS.map((g) => {
                const on = groups.includes(g.id);
                return (
                  <button
                    key={g.id}
                    onClick={() => toggleGroup(g.id)}
                    data-testid={`group-toggle-${g.id}`}
                    className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${
                      on
                        ? "border-[var(--al-purple)] bg-violet-50 text-slate-900 font-medium"
                        : "border-slate-200 hover:border-slate-300 text-slate-600"
                    }`}
                  >
                    <div className="font-display font-bold">{g.label}</div>
                    <div className="text-[11px] text-slate-500">{g.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-2 flex flex-wrap gap-3">
            <button
              onClick={start}
              disabled={busy}
              data-testid="mail-test-start"
              className="px-5 py-2.5 rounded-full al-grad-bg text-white font-display font-semibold text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              <Send className="h-4 w-4" /> Test starten
            </button>
          </div>
        </section>

        {/* Active runs */}
        {active.length > 0 && (
          <section className="al-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-[var(--al-purple)]" />
              <h2 className="font-display font-bold text-lg">Aktive Tests</h2>
            </div>
            <div className="space-y-2">
              {active.map((r) => {
                const recipient = typeof r === "string" ? r : r.email;
                const pending = typeof r === "string" ? null : r.pending;
                const nextAt = typeof r === "string" ? null : r.next_at;
                const endsAt = typeof r === "string" ? null : r.ends_at;
                return (
                  <div key={recipient} className="flex items-start justify-between gap-2 px-3 py-2.5 rounded-lg bg-violet-50 border border-violet-100">
                    <div className="text-sm text-slate-700 flex-1">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-[var(--al-purple)]" />
                        <span className="font-medium">{recipient}</span>
                        {pending != null && (
                          <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-white text-[var(--al-purple)] border border-violet-200">
                            {pending} ausstehend
                          </span>
                        )}
                      </div>
                      {(nextAt || endsAt) && (
                        <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap gap-x-3">
                          {nextAt && <span>nächste: {new Date(nextAt).toLocaleString("de-DE")}</span>}
                          {endsAt && <span>endet: {new Date(endsAt).toLocaleString("de-DE")}</span>}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => cancel(recipient)}
                      data-testid={`cancel-${recipient}`}
                      className="text-xs font-semibold text-rose-600 hover:text-rose-700 flex items-center gap-1 shrink-0"
                    >
                      <X className="h-3.5 w-3.5" /> Abbrechen
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Last summary / timeline */}
        {lastSummary && (
          <section className="al-card p-6">
            <h2 className="font-display font-bold text-lg mb-3">Geplante Mails ({lastSummary.total_mails})</h2>
            <p className="text-xs text-slate-500 mb-3">
              Voraussichtliche Gesamtdauer: ca. {Math.ceil(lastSummary.estimated_total_seconds / 60)} Min · Empfänger: <strong>{lastSummary.email}</strong>
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-slate-400">
                  <tr><th className="text-left py-2 pr-3">Serie</th><th className="text-left pr-3">Mail-ID</th><th className="text-left pr-3">Tag</th><th className="text-left">Versand nach (s)</th></tr>
                </thead>
                <tbody>
                  {lastSummary.timeline.map((row, i) => (
                    <tr key={`${row.mail_id}-${i}`} className="border-t border-slate-100">
                      <td className="py-1.5 pr-3 font-bold text-[var(--al-purple)]">{row.group}</td>
                      <td className="pr-3 font-mono text-xs">{row.mail_id}</td>
                      <td className="pr-3 text-slate-500">Tag {row.day_offset}</td>
                      <td className="text-slate-500">{row.scheduled_seconds}s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
